type LogLevel = "debug" | "error" | "info" | "warn" | "log";

/** Cap on serialized string length to avoid flooding the log pipeline. */
const MAX_STRING_LENGTH = 8_000;

/** Error properties extracted explicitly; everything else on the error is copied verbatim. */
const STANDARD_ERROR_KEYS = new Set(["name", "message", "stack"]);

const CLIENT_LOG_CONFIG: Record<LogLevel, { style: string; emoji: string }> = {
  debug: { style: "color: green", emoji: "🪲" },
  error: { style: "color: red", emoji: "🤬" },
  info: { style: "color: lightblue", emoji: "ℹ️" },
  warn: { style: "color: orange", emoji: "⚠️" },
  log: { style: "color: pink", emoji: "🪵" },
};

const isClient = () => typeof window !== "undefined";

const createLogFunction = (level: LogLevel) => {
  return (message: string, meta: Record<string, unknown> = {}) => {
    if (isClient()) {
      const { style, emoji } = CLIENT_LOG_CONFIG[level];
      return console[level](`%c${emoji} ${message}`, style, meta);
    }
    if (process.env.NODE_ENV === "development") {
      return console[level](message, meta);
    }
    // Production: emit a single JSON line so Kibana indexes it as one document
    // with structured, queryable fields instead of one row per pretty-printed line.
    return console.log(safeStringify({ level, message, ...meta }));
  };
};

/**
 * Circular-safe `JSON.stringify` that also unwraps `Error` instances — whose
 * `message`/`stack`/`name` are non-enumerable and would otherwise serialize to
 * `{}` — and caps oversized strings. Guarantees a single line of output.
 */
export function safeStringify(value: unknown): string {
  const seen = new WeakSet<object>();
  return JSON.stringify(value, (_key, val) => {
    if (val instanceof Error) {
      if (seen.has(val)) return "[Circular]";
      seen.add(val);
      return serializeError(val);
    }
    if (typeof val === "bigint") return val.toString();
    if (typeof val === "function") {
      return `[Function: ${val.name || "anonymous"}]`;
    }
    if (typeof val === "string" && val.length > MAX_STRING_LENGTH) {
      return `${val.slice(0, MAX_STRING_LENGTH)}…[truncated]`;
    }
    if (typeof val === "object" && val !== null) {
      if (seen.has(val)) return "[Circular]";
      seen.add(val);
    }
    return val;
  });
}

function serializeError(error: Error): Record<string, unknown> {
  const serialized: Record<string, unknown> = {
    name: error.name,
    message: error.message,
    stack: error.stack,
  };
  // Copy custom own-enumerable props (digest, statusCode, result, ...) so they
  // are not lost. Standard keys are already handled above.
  for (const key of Object.keys(error)) {
    if (STANDARD_ERROR_KEYS.has(key)) continue;
    serialized[key] = (error as unknown as Record<string, unknown>)[key];
  }
  return serialized;
}

export const debug = createLogFunction("debug");
export const error = createLogFunction("error");
export const info = createLogFunction("info");
export const warn = createLogFunction("warn");
export const log = createLogFunction("log");

// Default export for backward compatibility
export default { debug, error, info, warn, log };
